import {env} from "../../env/env";
import {DesignDto} from "../../generated/dashboard-client";
import {Button, Checkbox, Dropdown, Input, message, Drawer} from "antd";
import {DownOutlined, EditOutlined, MoreOutlined} from "@ant-design/icons";
import "./DesignCard.scss";
import React, {useState, useContext, useEffect, useRef} from "react";
import DesignApi from "../../api/DesignApi";
import {AppContext} from "../../AppContext";
import DesignCardMenu, {DesignOption, MenuMode} from "./DesignCardMenu";
import useFocus from "../../hooks/useFocus";
import {InUseModal} from "./InUseModal";
import {DuplicateNameModal} from "./DuplicateNameModal";
import {DesignInCartModal} from "./DesignInCartModal";
import {DesignInPrintProductionModal} from "./DesignInPrintProductionModal";
import {useRecoilState} from "recoil";
import {cartState, memberState, printProductionState} from "../../state/atoms";
import {isInUse} from "../../service/DesignService";
import {Spinner} from "../account/misc/Spinner";
import UserIcon from "../icons/UserIcon";
import {GetDesignEditLinkModal} from "./GetDesignEditLinkModal";
import SelectableCard from "../common/SelectableCard";
import RendererThumb from "../common/RendererThumb";
import {getSizeLabel, getSizeDimensions} from "../../util/utils";

export interface DesignCardProps {
  design: DesignDto;
  hasRemoveFolderOption: boolean;
  onSelect: (design: DesignDto, checked: boolean) => void;
  selectMode: boolean;
  isSelected: boolean;
  onOptionClick: (design: DesignDto, option: DesignOption) => void;
  onUpdate: (design: DesignDto) => void;
  reloadDesigns: (data: any) => void;
  scrollToDesignsTop: () => void;
  isSingleUserAcc: boolean;
  getDesignThumb: (design: DesignDto) => string;
  hasUsers: boolean;
  canSaveAsBrandTemplate: boolean;
}

export default function DesignCard({
                                     design,
                                     onSelect,
                                     selectMode,
                                     onOptionClick,
                                     onUpdate,
                                     hasRemoveFolderOption,
                                     isSelected,
                                     reloadDesigns,
                                     scrollToDesignsTop,
                                     isSingleUserAcc,
                                     getDesignThumb,
                                     hasUsers,
                                     canSaveAsBrandTemplate
                                   }: DesignCardProps) {
  const dropdownButton = useRef<HTMLButtonElement>(null);
  const {location} = useContext(AppContext);
  const [cart] = useRecoilState(cartState);
  const [printProduction] = useRecoilState(printProductionState);
  const [mode, setMode] = useState(MenuMode.Root);
  const [renameInputRef, setRenameInputFocus] = useFocus();
  const [isRenaming, setRenaming] = useState(false);
  const [name, setName] = useState("");
  const [isCopyLoading, setCopyLoading] = useState(false);
  const [isMenuVisible, setMenuVisible] = useState(false);
  const [isMobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [isInUseModalVisible, setInUseModalVisible] = useState(false);
  const [isDuplicateNameModalVisible, setDuplicateNameModalVisible] = useState(false);
  const [designInCartModal, setDesignInCartModal] = useState(false);
  const [designInPrintProductionModal, setDesignInPrintProductionModal] = useState(false);
  const [isGetDesignEditLinkModal, setDesignEditLinkModal] = useState(false);
  const [member] = useRecoilState(memberState);
  const [isInCart, setIsInCart] = useState(false);
  const [isInPrintProduction, setIsInPrintProduction] = useState(false);
  const [loading, setLoading] = useState(false);

  const isSharedInListings = design.sharedInListings === true;

  useEffect(() => {
    setName(design.name ?? "");
  }, [design]);

  useEffect(() => {
    setIsInPrintProduction(printProduction.menus.indexOf(design.id!) > -1);
  }, [printProduction.menus, design.id])

  useEffect(() => {
    setIsInCart(cart.menus.indexOf(design.id!) > -1);
  }, [cart.menus, design.id])

  function onDropdownVisibilityChange(visible: boolean) {
    if (visible) {
      setMode(MenuMode.Root);
    }
  }

  function rename() {
    setRenaming(false);
    if (!!name) {
      DesignApi.rename(design.id!, name, location.internalId!).then(() => {
        onUpdate({...design, name});
      }).catch((err) => {
        if (err.response.status === 400) {
          setDuplicateNameModalVisible(true);
        } else {
          setName(design.name!);
        }
      });
    }
  }

  function closeDuplicateNameModal() {
    setDuplicateNameModalVisible(false);
    setName(design.name!);
  }

  function startRenaming() {
    if (!selectMode) {
      setRenaming(true);
      (setRenameInputFocus as () => void)();
    }
  }

  async function editDesign() {
    if (design.upload || !design.id) return;
    if (selectMode) {
      onSelect(design, !isSelected);
      return;
    }

    if (!isSingleUserAcc) {
      let inUse = isInUse(design, member);
      if (!inUse) {
        setLoading(true);
        const updatedDesign = (await DesignApi.getLightDesign(design.id)).data;
        setLoading(false);
        inUse = isInUse(updatedDesign, member);
        onUpdate(updatedDesign);
      }
      if (inUse) {
        setInUseModalVisible(true);
        return;
      }
    }

    if (isInCart) {
      setDesignInCartModal(true);
      return;
    }

    if (isInPrintProduction) {
      setDesignInPrintProductionModal(true);
      return;
    }

    window.location.href = `${env.timberContextPath}/design?id=${design.id}`;
  }

  function closeInUseModal() {
    setInUseModalVisible(false);
  }

  function forceOpen() {
    window.location.href = `${env.timberContextPath}/design?id=${design.id}`;
  }

  function onCopyDesign() {
    setCopyLoading(true);
    DesignApi.copy(design.id!, `Copy of ${design.name}`).then((resp) => {
      setCopyLoading(false);
      scrollToDesignsTop();
      reloadDesigns(resp.data);
      setMenuVisible(false);
    }).catch(() => {
      setCopyLoading(false);
      message.error("Error copying design");
    });
  }

  function onCloseMobileMenu() {
    setMobileMenuVisible(false);
  }

  function hideDropdown() {
    dropdownButton.current?.click();
  }

  const cardMenu = (
    <DesignCardMenu
      hideDropdown={hideDropdown}
      design={design}
      hasRemoveFolderOption={hasRemoveFolderOption}
      onOptionClick={onOptionClick}
      mode={mode}
      setMode={setMode}
      isCopyLoading={isCopyLoading}
      onCopyDesign={onCopyDesign}
      closeMenu={() => setMenuVisible(false)}
      isInPrintProduction={isInPrintProduction}
      setDesignInPrintProductionModal={setDesignInPrintProductionModal}
      setDesignEditLinkModal={setDesignEditLinkModal}
      editDesign={editDesign}
      hasUsers={hasUsers}
      canSaveAsBrandTemplate={canSaveAsBrandTemplate}
      isSharedInListings={isSharedInListings}
    />
  );

  const canSelect = !member.viewOnly && !member.itemLibraryOnly && !member.dataOnly;

  const selectionSlot = canSelect ? (
    <Checkbox
      checked={isSelected}
      onChange={(ev) => onSelect(design, ev.target.checked)}
    />
  ) : null;

  const actionsSlot = (
    <>
      <Dropdown
        className={"desktop-design-menu more-btn-ops"}
        visible={isMenuVisible}
        overlay={cardMenu}
        onVisibleChange={onDropdownVisibilityChange}
      >
        <MoreOutlined
          ref={dropdownButton}
          onClick={() => setMenuVisible(!isMenuVisible)}
        />
      </Dropdown>
      <div className={"mobile-design-menu more-btn-ops"}>
        <MoreOutlined onClick={() => setMobileMenuVisible(!isMobileMenuVisible)} />
        <Drawer
          placement={"bottom"}
          visible={isMobileMenuVisible}
          onClose={onCloseMobileMenu}
          closeIcon={<DownOutlined />}
          className={"mobile-bottom-menu"}
          height={"450px"}
        >
          {cardMenu}
        </Drawer>
      </div>
    </>
  );

  const mediaSlot = (
    <>
      {loading && <div className={"spinner"}><Spinner /></div>}
      <RendererThumb
        src={getDesignThumb(design)}
        alt={design.name}
        loading="lazy"
        fallbackSrc={`${env.dashboardPath}/thumb-placeholder.png`}
        fallbackStyle={{ maxHeight: "15%" }}
      />
      <div className={`in-use-overlay ${!isSingleUserAcc && isInUse(design, member) ? "in-use-show" : ""}`}>
        <div>{design.inUse !== "-" ? design.inUse : <UserIcon />}</div>
      </div>
    </>
  );

  const sizeLabel = getSizeLabel(design.size ?? "");
  const sizeDims = getSizeDimensions(design.size ?? "");
  const unitSuffix = design.unit ? ` ${design.unit}` : "";
  const hasCustomSize = !sizeLabel && !!design.width && !!design.height;

  const footerSlot = (
    <>
      {!isRenaming && (
        <div
          className={`edit-name ${selectMode ? "disable-rename" : ""}`}
          onClick={startRenaming}
        >
          <h6>{design.name}</h6>
          <Button type={"link"} className={"rename-trigger"}>
            <EditOutlined />
          </Button>
        </div>
      )}
      <div className={`editing-name ${isRenaming && !selectMode ? "show" : ""}`}>
        <Input
          ref={renameInputRef}
          value={name}
          onChange={(ev) => setName(ev.target.value)}
          onBlur={rename}
          onPressEnter={(ev) => (ev.target as HTMLInputElement).blur()}
        />
      </div>
      {sizeLabel ? (
        <span className={"design-card-size"}>
          <span className={"size-label"}>{sizeLabel}</span> • {sizeDims}{unitSuffix}
        </span>
      ) : hasCustomSize ? (
        <span className={"design-card-size"}>
          <span className={"size-label"}>Custom</span> • {design.width} x {design.height}{unitSuffix}
        </span>
      ) : null}
    </>
  );

  return (
    <SelectableCard
      className={`design-card${design.upload ? " pdf-upload" : ""}`}
      selected={isSelected}
      selectMode={selectMode}
      selectionSlot={selectionSlot}
      actionsSlot={actionsSlot}
      mediaSlot={mediaSlot}
      footerSlot={footerSlot}
      onMediaClick={editDesign}
    >
      <InUseModal isVisible={isInUseModalVisible} onClose={closeInUseModal} onOpenAnyway={forceOpen} />
      <DuplicateNameModal isVisible={isDuplicateNameModalVisible} onClose={closeDuplicateNameModal} name={name} />
      <DesignInCartModal
        isVisible={designInCartModal}
        open={forceOpen}
        onClose={() => setDesignInCartModal(false)}
      />
      <DesignInPrintProductionModal
        isVisible={designInPrintProductionModal}
        onClose={() => setDesignInPrintProductionModal(false)}
        open={forceOpen}
      />
      <GetDesignEditLinkModal
        isVisible={isGetDesignEditLinkModal}
        onClose={() => setDesignEditLinkModal(false)}
        design={design}
      />
    </SelectableCard>
  );
}
